import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, ArrowLeft } from "lucide-react";
import { useAuth, apiError } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Wordmark, Footer, Shield } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function SetPassword() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const mode = params.get("mode") === "reset" ? "reset" : "activate";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const isReset = mode === "reset";
  const title = isReset ? "Choose a new password" : "Set your WarRoom password";
  const cta = isReset ? "Update password" : "Set password & continue";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) { setError("This link is missing its security token. Please use the link from your email."); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      try { sessionStorage.removeItem("wr:intakeBannerDismissed"); } catch (_) { /* ignore */ }
      setUser(data);
      setDone(true);
      toast.success(isReset ? "Password updated." : "Password set. Welcome to your WarRoom.");
      setTimeout(() => {
        if (data.role === "operator") navigate("/operator");
        else if (!data.onboarding_complete) navigate("/intake");
        else navigate("/app/today");
      }, 900);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-wr-bg flex flex-col wr-grid-bg" data-testid="set-password-page">
      <header className="border-b border-wr-border bg-black/70 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <Wordmark size="md" />
          <Link to="/login" className="wr-label hover:text-white flex items-center gap-2" data-testid="setpw-signin-link">
            <ArrowLeft size={14} /> Sign in
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md bg-wr-surface border border-wr-border p-7 sm:p-9 wr-fade-up">
          {done ? (
            <div data-testid="setpw-done">
              <CheckCircle2 className="h-10 w-10 text-emerald-400" />
              <h1 className="wr-heading font-bold text-white text-2xl mt-4">You're all set.</h1>
              <p className="text-sm text-wr-sub mt-2">Signing you in and taking you to your WarRoom…</p>
            </div>
          ) : (
            <>
              <Shield className="h-12 w-12 mb-4" />
              <p className="wr-label !text-wr-red">▸ SARGE · SECURE ACCESS</p>
              <h1 className="wr-heading font-bold text-white text-2xl mt-3">{title}</h1>
              <p className="text-sm text-wr-sub mt-2">
                {isReset
                  ? "Enter a new password for your account. This secure link works once."
                  : "Create a password to sign in and start Sarge's first-day intake. This secure link works once."}
              </p>
              <form onSubmit={submit} className="mt-7 space-y-5" data-testid="setpw-form">
                <div>
                  <label className="wr-label">New password</label>
                  <Input
                    required type="password" data-testid="setpw-password" value={password}
                    onChange={(e) => setPassword(e.target.value)} minLength={8}
                    className="mt-1.5 bg-wr-bg border-wr-border rounded-none focus-visible:ring-0"
                  />
                </div>
                <div>
                  <label className="wr-label">Confirm password</label>
                  <Input
                    required type="password" data-testid="setpw-confirm" value={confirm}
                    onChange={(e) => setConfirm(e.target.value)} minLength={8}
                    className="mt-1.5 bg-wr-bg border-wr-border rounded-none focus-visible:ring-0"
                  />
                </div>
                {error && <p className="text-sm text-wr-red wr-mono" data-testid="setpw-error">{error}</p>}
                <Button
                  type="submit" disabled={busy} data-testid="setpw-submit"
                  className="w-full rounded-none bg-wr-red hover:bg-wr-redHover text-white wr-mono uppercase tracking-wider h-11"
                >
                  {busy ? "Saving…" : cta}
                </Button>
              </form>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
